import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { InvitationResentAction } from '@Application/Users/Users/Actions/invitationResent';
import type { InvitationRevokedAction } from '@Application/Users/Users/Actions/invitationRevoked';
import type { PasswordResetRequestedAction } from '@Application/Users/Users/Actions/passwordResetRequested';
import type { RoleImpactedUsersFetchFailedAction } from '@Application/Users/Users/Actions/roleImpactedUsersFetchFailed';
import type { RoleImpactedUsersFetchedAction } from '@Application/Users/Users/Actions/roleImpactedUsersFetched';
import type { UserActivatedAction } from '@Application/Users/Users/Actions/userActivated';
import type { UserDeactivatedAction } from '@Application/Users/Users/Actions/userDeactivated';
import type { UserDeletedAction } from '@Application/Users/Users/Actions/userDeleted';
import type { UserInvitedAction } from '@Application/Users/Users/Actions/userInvited';
import type { UserMfaResetAction } from '@Application/Users/Users/Actions/userMfaReset';
import type { UserUpdatedAction } from '@Application/Users/Users/Actions/userUpdated';
import type { UsersFetchedAction } from '@Application/Users/Users/Actions/usersFetched';
import RoleImpactedUsersListView from '@UI/Store/Users/Views/RoleImpactedUsersListView';
import UserView from '@UI/Store/Users/Views/UserView';
import UsersListView from '@UI/Store/Users/Views/UsersListView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type UsersStore = {
	users: UsersListView | null;
	// Its own location, distinct from `users`: the revoke-permission confirmation's preview list of a
	// role's carriers must never overwrite the Users screen's own list, and vice versa.
	roleImpactedUsers: RoleImpactedUsersListView | null;
};

const initialState: UsersStore = {
	users: null,
	roleImpactedUsers: null,
};

/** Every mutation action carries the same `UserPayload` shape: one handler template covers all eight. */
type UserMutatedAction =
	| UserInvitedAction
	| UserUpdatedAction
	| InvitationResentAction
	| InvitationRevokedAction
	| PasswordResetRequestedAction
	| UserActivatedAction
	| UserDeactivatedAction
	| UserMfaResetAction;

type Actions =
	| UsersFetchedAction
	| UserMutatedAction
	| UserDeletedAction
	| UserRevokedAction
	| RoleImpactedUsersFetchedAction
	| RoleImpactedUsersFetchFailedAction;

const whenUsersFetched = (store: UsersStore, action: UsersFetchedAction): UsersStore => ({
	...store,
	users: UsersListView.replaceAll(
		action.payload.results.map((user) => new UserView(user)),
		action.accountId,
		action.payload.totalRecords,
	),
});

const whenRoleImpactedUsersFetched = (
	store: UsersStore,
	action: RoleImpactedUsersFetchedAction,
): UsersStore => ({
	...store,
	roleImpactedUsers: new RoleImpactedUsersListView(
		action.payload.results.map((user) => new UserView(user)),
		action.accountId,
		action.roleId,
		action.payload.totalRecords,
	),
});

// A failed preview read clears any previous role's stale data rather than leaving it displayed under
// a permission it no longer describes.
const whenRoleImpactedUsersFetchFailed = (store: UsersStore): UsersStore => ({
	...store,
	roleImpactedUsers: null,
});

const whenUserInvited = (store: UsersStore, action: UserInvitedAction): UsersStore => {
	if (!store.users || store.users.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		users: store.users.addUser(new UserView(action.payload)),
	};
};

const whenUserMutated = (store: UsersStore, action: UserMutatedAction): UsersStore => {
	if (!store.users || store.users.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		users: store.users.updateUser(new UserView(action.payload)),
	};
};

const whenUserDeleted = (store: UsersStore, action: UserDeletedAction): UsersStore => {
	if (!store.users || store.users.accountId !== action.accountId) {
		return store;
	}

	return {
		...store,
		users: store.users.removeUser(action.payload.userId),
	};
};

const whenUserRevoked = (): UsersStore => initialState;

export default registerStore<'usersStore', Actions>(
	'usersStore',
	createSliceReducer({
		USERS_FETCHED: whenUsersFetched,
		USER_INVITED: whenUserInvited,
		USER_UPDATED: whenUserMutated,
		INVITATION_RESENT: whenUserMutated,
		INVITATION_REVOKED: whenUserMutated,
		PASSWORD_RESET_REQUESTED: whenUserMutated,
		USER_ACTIVATED: whenUserMutated,
		USER_DEACTIVATED: whenUserMutated,
		USER_MFA_RESET: whenUserMutated,
		USER_DELETED: whenUserDeleted,
		USER_REVOKED: whenUserRevoked,
		ROLE_IMPACTED_USERS_FETCHED: whenRoleImpactedUsersFetched,
		ROLE_IMPACTED_USERS_FETCH_FAILED: whenRoleImpactedUsersFetchFailed,
	}),
	initialState,
);
